import { ExternalLink, Heart, Trash2, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import type { Product } from "@/components/DesignResults";
import { track, affiliateProps, productItemId, parsePriceNumber } from "@/lib/posthog";
import { trackBrevo, touchActivity } from "@/lib/brevo";

interface SavedItemsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  savedItems: Product[];
  onRemove: (product: Product) => void;
  onClearAll: () => void;
}

const SavedItemsSheet = ({
  open,
  onOpenChange,
  savedItems,
  onRemove,
  onClearAll,
}: SavedItemsSheetProps) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display text-xl flex items-center gap-2">
            <Heart className="w-5 h-5" /> Saved Items
          </SheetTitle>
          <SheetDescription>
            {savedItems.length
              ? `${savedItems.length} item${savedItems.length > 1 ? "s" : ""} saved`
              : "No items saved yet"}
          </SheetDescription>
        </SheetHeader>

        {savedItems.length > 0 && (
          <div className="mt-6 space-y-4">
            {savedItems.map((product, i) => (
              <div
                key={`${product.title}-${i}`}
                className="flex gap-3 p-3 bg-card border border-border"
              >
                {product.thumbnail && (
                  <img
                    src={product.thumbnail}
                    alt={product.title}
                    className="w-16 h-16 object-cover bg-muted shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-foreground line-clamp-2">
                    {product.title}
                  </h4>
                  <p className="text-foreground font-semibold text-sm mt-0.5">
                    {product.price}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5">
                      {product.source}
                    </span>
                    {product.link && (
                      <a
                        href={product.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          track("outbound_link_clicked", {
                            retailer: product.source,
                            item_id: productItemId(product),
                            item_price: parsePriceNumber(product.price),
                            link_type: "view",
                            source: "saved_items_sheet",
                            ...affiliateProps(product.link),
                          });
                          void trackBrevo("shopping_list_clicked", {
                            item_id: productItemId(product),
                            item_brand: product.source,
                          });
                          touchActivity();
                        }}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onRemove(product)}
                  className="shrink-0 p-1.5 text-muted-foreground hover:text-destructive transition-colors self-start"
                  aria-label="Remove item"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}

            <p className="text-[11px] leading-relaxed text-muted-foreground text-center">
              Some links are affiliate links. If you buy through them we may earn a small commission, at no extra cost to you.
            </p>

            <button
              onClick={onClearAll}
              className="w-full py-2 text-xs text-muted-foreground hover:text-foreground transition-colors tracking-normal"
            >
              Clear All
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default SavedItemsSheet;
